import type { ReactElement } from 'react';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { NotificationsProvider, useMarkViewReadOnVisit } from './Notifications';
import { Sidebar } from './Sidebar';
import { SidebarProvider, useSidebar } from './SidebarToggle';
import { Topbar } from './Topbar';

/**
 * The application frame: sidebar, topbar, and the routed content region.
 *
 * Mirrors the prototype's static body markup (`app`, `sidebar`, `main`,
 * `topbar`, `content`) so the vendored stylesheet lays it out unchanged.
 *
 * Where the prototype re-rendered all three regions on every state change by
 * reassigning `innerHTML`, only the routed content changes here. That removes
 * the manual listener-cleanup arrays the prototype needed (`bkCleanup`,
 * `calCleanup`) — the pattern its author flagged as most at risk of leaking.
 *
 * Theme and language live in `PreferencesProvider`, above the router, so the
 * sign-in screen has them too.
 */
export function AppShell(): ReactElement {
  return (
    <SidebarProvider>
      <NotificationsProvider>
        <Frame />
      </NotificationsProvider>
    </SidebarProvider>
  );
}

/**
 * Inside the provider, so the scrim can read whether the sidebar is showing.
 *
 * The scrim is only rendered when it is needed. A permanently mounted overlay
 * that CSS hides above the breakpoint is one `display` rule away from covering
 * the whole desktop page and swallowing every click.
 */
function Frame(): ReactElement {
  const { t } = useTranslation();
  const { isOpen, close } = useSidebar();

  /*
   * REQ-119 — here rather than in each page, so a screen cannot be added
   * without its badge clearing when someone opens it.
   */
  useMarkViewReadOnVisit();

  return (
    <>
      {/*
        Skip link — the first thing a keyboard user reaches. Without it, getting
        to the page content means tabbing through every navigation item on
        every single screen.
      */}
      <a className="skip-link" href="#content">
        {t('skip_to_content')}
      </a>

      <div className="app">
        <Sidebar />

        {isOpen ? (
          <div
            className="scrim open"
            onClick={close}
            /* Decorative: the dismiss path for a keyboard user is Escape, and
               the toggle itself is still reachable. */
            aria-hidden="true"
          />
        ) : null}

        <div className="main">
          <Topbar />
          <main id="content" className="content" tabIndex={-1}>
            <Outlet />
          </main>
        </div>
      </div>
    </>
  );
}
